import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Calendar, Plus, Clock, Loader2, ChevronRight } from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import CreateDiaHabilModal from "./CreateDiaHabilModal";
import Swal from "sweetalert2";
import { obtenerHorariosZonaComun, HorarioZonaComun } from "../../../../api/zonaComunHorarioService";

export default function DiasHabilesZonaComunPage() {
  const { session } = useAuth();
  const { codigo } = useParams<{ codigo: string }>();
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [zonaComunCodigo, setZonaComunCodigo] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [diasHabiles, setDiasHabiles] = useState<HorarioZonaComun[]>([]);

  // Obtener el código de zona común desde la URL
  useEffect(() => {
    if (codigo) {
      setZonaComunCodigo(codigo);
      loadDiasHabiles(codigo);
    }
  }, [codigo]);

  const loadDiasHabiles = async (zcCodigo: string) => {
    try {
      setIsLoading(true);
      const response = await obtenerHorariosZonaComun({
        codigo: "", // No se usa para obtener todos, pero la interfaz lo pide
        "zona comun codigo": zcCodigo,
        "dia de utilizacion codigo": "" // Para obtener todos
      });

      if (response.code === "1" && response.object_) {
        setDiasHabiles(response.object_);
      }
    } catch (error) {
      console.error("Error al cargar días hábiles:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (!zonaComunCodigo || zonaComunCodigo.trim() === "") {
      Swal.fire({
        icon: "warning",
        title: "Código de zona común requerido",
        text: "No se ha especificado el código de zona común. Por favor, asegúrese de acceder a esta página con el código de zona común correcto.",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }
    setShowCreateModal(true);
  };

  const handleCloseModal = (created: boolean) => {
    setShowCreateModal(false);
    if (created && zonaComunCodigo) {
      loadDiasHabiles(zonaComunCodigo);
    }
  };

  const handleNavigateToTurnos = (dia: HorarioZonaComun) => {
    navigate(`/zona-comun/${zonaComunCodigo}/dias-habiles/${dia.zhd_codigo}/turnos`, {
      state: {
        zonaComunNombre: dia.zhd_zona_comun, // Asumiendo que viene el nombre, si no, habrá que pasarlo de otra forma
        diaNombre: dia.zhd_dia_de_utilizacion,
        horarioDia: dia
      }
    });
  };

  return (
    <div className="flex min-h-screen bg-gray-100 text-gray-900">
      <Sidebar />
      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-military-600">
            Días Hábiles Zona Común
          </h2>
          <Button
            onClick={handleOpenModal}
            className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2"
          >
            <Plus size={20} />
            <span className="hidden sm:inline">Agregar Día Hábil</span>
            <span className="sm:hidden">Agregar</span>
          </Button>
        </div>

        <Card className="shadow-lg border-l-4 border-military-500">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-xl text-military-600 flex items-center gap-2">
                <Calendar size={24} />
                Lista de Días Hábiles
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-military-600" />
                <span className="ml-2 text-gray-600">Cargando días hábiles...</span>
              </div>
            ) : diasHabiles.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Calendar size={48} className="mx-auto mb-4 text-gray-400" />
                <p>No hay días hábiles configurados aún.</p>
                <p className="text-sm mt-2 mb-4">
                  Haz clic en el botón "Agregar Día Hábil" para comenzar.
                </p>
                <Button
                  onClick={handleOpenModal}
                  className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2 mx-auto"
                >
                  <Plus size={20} />
                  Agregar Primer Día Hábil
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {diasHabiles.map((dia) => (
                  <div 
                    key={dia.zhd_codigo}
                    onClick={() => handleNavigateToTurnos(dia)}
                    className="flex flex-col p-4 rounded-lg border border-gray-200 bg-white hover:border-military-500 hover:shadow-md cursor-pointer transition-all group relative overflow-hidden"
                  >
                     <div className="absolute top-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity text-military-500">
                        <ChevronRight size={20} />
                     </div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="bg-military-100 p-2 rounded-full text-military-600 group-hover:bg-military-500 group-hover:text-white transition-colors">
                        <Calendar size={20} />
                      </div>
                      <h3 className="font-bold text-lg text-gray-800">
                        {dia.zhd_dia_de_utilizacion}
                      </h3>
                    </div>
                    
                    <div className="space-y-1 text-sm text-gray-600 pl-11">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-military-700">Apertura:</span>
                        <span>{dia.zhd_hora_apertura}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-military-700">Cierre:</span>
                        <span>{dia.zhd_hora_cierre}</span>
                      </div>
                    </div>
                    
                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center pl-1">
                      <span className="text-xs font-semibold text-military-600 bg-military-50 px-2 py-1 rounded">
                        Código: {dia.zhd_codigo}
                      </span>
                      <span className="text-xs text-gray-400 group-hover:text-military-500 font-medium">
                        Ver Turnos
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <button
          onClick={handleOpenModal}
          className="fixed bottom-6 right-6 bg-military-500 hover:bg-military-600 text-white rounded-full p-4 shadow-2xl z-50 md:hidden"
          aria-label="Agregar día hábil"
        >
          <Plus size={24} />
        </button>

        <CreateDiaHabilModal
          isOpen={showCreateModal}
          zonaComunCodigo={zonaComunCodigo}
          onClose={handleCloseModal}
        />
      </main>
    </div>
  );
}

